import { normalizeName } from '../shared/naming';

/**
 * Mapeamento de colunas de planilha — a parte que **todo** cadastro importado repete.
 *
 * A importação de materiais (Fase 3) provou o formato: uma lista de colunas com sinônimos,
 * um mapeamento sugerido a partir dos cabeçalhos e a tela deixando corrigir antes de
 * gravar. O que muda de um cadastro para outro são os campos e as regras de cada linha —
 * não a forma de casar "Descrição" com `name`.
 *
 * Este módulo é o pedaço comum, genérico no conjunto de campos. Um cadastro novo declara
 * suas colunas e ganha sugestão de mapeamento, leitura de célula e contagem de status sem
 * escrever nada disso de novo.
 */

export interface ImportColumnDef<F extends string> {
  readonly field: F;
  readonly label: string;
  readonly required: boolean;
  /** Cabeçalhos que costumam nomear esta coluna nas planilhas do escritório. */
  readonly synonyms: readonly string[];
}

export type ColumnMappingOf<F extends string> = Partial<Record<F, string>>;

/** Compara cabeçalhos ignorando acento, caixa e pontuação — "Salário" casa com "salario". */
export function headerKey(value: string): string {
  return normalizeName(value)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * Sugere o mapeamento a partir dos cabeçalhos do arquivo.
 *
 * É sugestão, não decisão: a tela mostra o que foi entendido e deixa corrigir antes de
 * qualquer gravação. Uma coluna só serve a um campo — o primeiro que a reivindicar.
 */
export function suggestMapping<F extends string>(
  headers: readonly string[],
  columns: readonly ImportColumnDef<F>[],
): ColumnMappingOf<F> {
  const mapping: ColumnMappingOf<F> = {};
  const taken = new Set<string>();

  for (const column of columns) {
    const match = headers.find((header) => {
      if (taken.has(header)) return false;
      const key = headerKey(header);
      return key === headerKey(column.label) || column.synonyms.includes(key);
    });
    if (match !== undefined) {
      mapping[column.field] = match;
      taken.add(match);
    }
  }

  return mapping;
}

/** Lê a célula de um campo na linha, já sem espaços nas pontas. */
export function cellReader<F extends string>(
  raw: Record<string, string>,
  mapping: ColumnMappingOf<F>,
): (field: F) => string {
  return (field) => {
    const column = mapping[field];
    return column === undefined ? '' : (raw[column] ?? '').trim();
  };
}

/**
 * Destino de uma linha do arquivo.
 *
 * O mesmo vocabulário do catálogo de materiais, porque é o mesmo gesto: quem importa
 * quer saber quantos entram, quantos mudam e o que está errado — antes de gravar.
 */
export type ImportRowStatus = 'CREATE' | 'UPDATE' | 'UNCHANGED' | 'INVALID' | 'DUPLICATE';

export const IMPORT_STATUS_LABEL: Readonly<Record<ImportRowStatus, string>> = {
  CREATE: 'Novo',
  UPDATE: 'Atualiza',
  UNCHANGED: 'Sem mudança',
  INVALID: 'Com erro',
  DUPLICATE: 'Repetido',
};

export interface ImportIssueOf<F extends string> {
  readonly field: F | 'row';
  readonly message: string;
}

export interface ImportSummary {
  readonly total: number;
  readonly create: number;
  readonly update: number;
  readonly unchanged: number;
  readonly invalid: number;
  readonly duplicate: number;
}

export function summarizeRows(rows: readonly { status: ImportRowStatus }[]): ImportSummary {
  return {
    total: rows.length,
    create: rows.filter((row) => row.status === 'CREATE').length,
    update: rows.filter((row) => row.status === 'UPDATE').length,
    unchanged: rows.filter((row) => row.status === 'UNCHANGED').length,
    invalid: rows.filter((row) => row.status === 'INVALID').length,
    duplicate: rows.filter((row) => row.status === 'DUPLICATE').length,
  };
}

/** `+1` pelo cabeçalho e `+1` porque quem abre a planilha conta a partir de 1. */
export function rowNumberOf(index: number): number {
  return index + 2;
}

/**
 * Dinheiro escrito à mão numa planilha.
 *
 * `"R$ 3.520,00"`, `"3520"`, `"3.520,50"` e `"3520.50"` são a mesma folha. O que decide é
 * o **último** separador: em `3.520,50` a vírgula é decimal; em `3,520.50` é o ponto.
 * Devolve `undefined` para célula vazia — salário ausente é estado legítimo (RN-055) —, e
 * `null` para texto que não é número, que é erro de linha.
 */
export function parseImportMoney(raw: string): number | undefined | null {
  const trimmed = raw.trim();
  if (trimmed === '') return undefined;

  // Cai o enfeite de moeda e todo espaço — `\s` já cobre o espaço fixo que o Excel
  // deixa em "R$ 3.520,00" copiado da web. O resto **tem** de ser número: "a combinar"
  // é erro de linha, e virar "sem salário" esconderia o problema em vez de mostrá-lo.
  const text = trimmed.replace(/R\$/gi, '').replace(/\s/g, '');
  if (text === '' || !/^-?[\d.,]+$/.test(text)) return null;

  const lastComma = text.lastIndexOf(',');
  const lastDot = text.lastIndexOf('.');
  let normalized: string;

  if (lastComma === -1 && lastDot === -1) {
    normalized = text;
  } else if (lastComma > lastDot) {
    normalized = text.replace(/\./g, '').replace(',', '.');
  } else {
    normalized = text.replace(/,/g, '');
  }

  const value = Number(normalized);
  if (!Number.isFinite(value) || value < 0) return null;
  return value;
}
